import { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { PercentageInput } from "@/components/ui/percentage-input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Form, FormField, FormItem, FormLabel, FormControl, FormMessage,
} from "@/components/ui/form";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useDebounce } from "@/lib/hooks/use-debounce";
import {
  useCategories, useTags, useCreatePremium, useUpdatePremium, usePremiumGroups,
} from "../queries/catalog-queries";
import { usePremiums } from "../queries/premium-queries";
import { premiumSchema } from "../schemas/catalog-schemas";
import type { PremiumFormValues } from "../schemas/catalog-schemas";
import type { PremiumTypeSlug } from "../queries/premium-queries";
import type { Premium, Category, Tag, PremiumGroup, WoodSpecies, DoorStyle } from "../types/catalog.types";

const TYPE_LABELS: Record<string, string> = {
  door_style: "Door Style",
  wood_species: "Wood Species",
  finish_option: "Finish Option",
  finished_side: "Finished Side",
};

interface PremiumFormDrawerProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  premiumType: PremiumTypeSlug;
  premium: Premium | null;
}

export function PremiumFormDrawer({ open, onOpenChange, premiumType, premium }: PremiumFormDrawerProps) {
  const isEdit = !!premium;

  const form = useForm<PremiumFormValues>({
    resolver: zodResolver(premiumSchema) as never,
    defaultValues: {
      code: "",
      description: "",
      export_name: "",
      price: "",
      percentage: "",
      categories: [],
      tags: [],
      group: "",
      index: "",
      finish_options: [],
      wood_species: [],
      _premiumType: premiumType,
    },
  });

  const [catSearch, setCatSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [foSearch, setFoSearch] = useState("");
  const [wsSearch, setWsSearch] = useState("");
  const debouncedCatSearch = useDebounce(catSearch, 300);

  const { data: catData } = useCategories({
    name: debouncedCatSearch || undefined,
    page_size: 100,
  });
  const allCategories = useMemo<Category[]>(
    () => (Array.isArray(catData) ? catData : (catData as { results?: Category[] })?.results ?? []) as Category[],
    [catData],
  );

  const { data: tagData } = useTags();
  const allTags = (Array.isArray(tagData) ? tagData : (tagData as { results?: Tag[] })?.results ?? []) as Tag[];

  const { data: groupData } = usePremiumGroups(premiumType);
  const allGroups = (Array.isArray(groupData) ? groupData : (groupData as { results?: PremiumGroup[] })?.results ?? []) as PremiumGroup[];

  const { data: foData } = usePremiums("finish_option", { all: 1 }, premiumType === "wood_species" && open);
  const finishOptionsList = (Array.isArray(foData) ? foData : (foData as { results?: Premium[] })?.results ?? []) as Premium[];

  const { data: wsData } = usePremiums("wood_species", { all: 1 }, premiumType === "door_style" && open);
  const woodSpeciesList = (Array.isArray(wsData) ? wsData : (wsData as { results?: Premium[] })?.results ?? []) as Premium[];

  const createMut = useCreatePremium(premiumType);
  const updateMut = useUpdatePremium(premiumType);

  useEffect(() => {
    if (open) {
      if (premium) {
        form.reset({
          code: premium.code,
          description: premium.description ?? "",
          export_name: premium.export_name ?? "",
          price: premium.price ?? "",
          percentage: premium.percentage ?? "",
          categories: premium.categories?.map((c) => c.id) ?? [],
          tags: premium.tags?.map((t) => ({ name: t.name, value: t.value ?? "" })) ?? [],
          group: premium.group ? String(premium.group) : "",
          index: premium.index != null ? String(premium.index) : "",
          finish_options:
            premiumType === "wood_species"
              ? (premium as WoodSpecies).finish_options?.map((f) => f.id) ?? []
              : [],
          wood_species:
            premiumType === "door_style"
              ? (premium as DoorStyle).wood_species?.map((w) => w.id) ?? []
              : [],
          _premiumType: premiumType,
        });
      } else {
        form.reset({
          code: "",
          description: "",
          export_name: "",
          price: "",
          percentage: "",
          categories: [],
          tags: [],
          group: "",
          index: "",
          finish_options: [],
          wood_species: [],
          _premiumType: premiumType,
        });
      }
      setCatSearch("");
      setTagSearch("");
      setFoSearch("");
      setWsSearch("");
    }
  }, [open, premium, premiumType, form]);

  function onSubmit(values: PremiumFormValues) {
    const payload: Record<string, unknown> = {
      code: values.code.trim(),
      description: values.description?.trim() ?? "",
      export_name: values.export_name?.trim() ?? "",
      price: values.price || null,
      percentage: values.percentage || null,
      categories: values.categories,
      tags: (values.tags ?? []).filter((t) => t.name),
      group: values.group ? Number(values.group) : null,
      index: values.index ? Number(values.index) : 0,
    };

    if (premiumType === "wood_species") {
      payload.finish_options = values.finish_options;
    }
    if (premiumType === "door_style") {
      payload.wood_species = values.wood_species;
    }

    if (isEdit) {
      updateMut.mutate({ id: premium.id, payload }, {
        onSuccess: () => { toast.success(`${TYPE_LABELS[premiumType]} updated`); onOpenChange(false); },
        onError: () => toast.error("Update failed"),
      });
    } else {
      createMut.mutate(payload, {
        onSuccess: () => { toast.success(`${TYPE_LABELS[premiumType]} created`); onOpenChange(false); },
        onError: () => toast.error("Create failed"),
      });
    }
  }

  const saving = createMut.isPending || updateMut.isPending;

  const [knownCategories, setKnownCategories] = useState<Map<number, Category>>(
    () => new Map(premium?.categories?.map((c) => [c.id, c as Category]) ?? []),
  );
  useEffect(() => {
    setKnownCategories((prev) => {
      const next = new Map(prev);
      allCategories.forEach((c) => next.set(c.id, c));
      premium?.categories?.forEach((c) => {
        if (!next.has(c.id)) next.set(c.id, c as Category);
      });
      return next;
    });
  }, [allCategories, premium]);

  const filteredCats = allCategories;

  const filteredTags = tagSearch
    ? allTags.filter((t) => t.name.toLowerCase().includes(tagSearch.toLowerCase()))
    : allTags;

  const filteredFo = foSearch
    ? finishOptionsList.filter((f) => f.code.toLowerCase().includes(foSearch.toLowerCase()))
    : finishOptionsList;

  const filteredWs = wsSearch
    ? woodSpeciesList.filter((w) => w.code.toLowerCase().includes(wsSearch.toLowerCase()))
    : woodSpeciesList;

  function toggleInList(field: "categories" | "finish_options" | "wood_species", id: number) {
    const current = form.getValues(field) ?? [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    form.setValue(field, next, { shouldValidate: field === "categories" });
  }

  function toggleTag(tag: Tag) {
    const current = form.getValues("tags") ?? [];
    const exists = current.some((t) => t.name === tag.name);
    if (exists) {
      form.setValue("tags", current.filter((t) => t.name !== tag.name));
    } else {
      form.setValue("tags", [...current, { name: tag.name, value: tag.value ?? "" }]);
    }
  }

  const selectedCategories = form.watch("categories");
  const selectedTags = form.watch("tags") ?? [];
  const selectedFinishOptions = form.watch("finish_options") ?? [];
  const selectedWoodSpecies = form.watch("wood_species") ?? [];
  const exportNameRequired = premiumType === "door_style" || premiumType === "finish_option";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="!w-[75vw] !max-w-[900px] flex flex-col">
        <SheetHeader className="border-b pb-4">
          <SheetTitle>{isEdit ? "Edit" : "Add"} {TYPE_LABELS[premiumType]}</SheetTitle>
          <SheetDescription>
            {isEdit ? `Editing ${premium.code}` : `Create a new ${TYPE_LABELS[premiumType].toLowerCase()}`}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Code */}
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Premium code" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Categories multi-select */}
              <FormField
                control={form.control}
                name="categories"
                render={() => (
                  <FormItem>
                    <FormLabel>Categories *</FormLabel>
                    <FormControl>
                      <MultiSelectPopover
                        items={filteredCats}
                        selected={selectedCategories}
                        onToggle={(id) => toggleInList("categories", id)}
                        search={catSearch}
                        onSearchChange={setCatSearch}
                        placeholder="Select categories..."
                        searchPlaceholder="Search categories..."
                        getLabel={(c) => c.name}
                        selectedLabels={
                          new Map(
                            Array.from(knownCategories.entries()).map(([id, c]) => [id, c.name]),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Export Name */}
              <FormField
                control={form.control}
                name="export_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Export Name{exportNameRequired ? " *" : ""}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Export name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Group + Index */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="group"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Group</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={(v) => v && field.onChange(v)}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select group..." />
                          </SelectTrigger>
                          <SelectContent>
                            {allGroups.map((g) => (
                              <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="index"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Index</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} placeholder="0" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Price / Percentage */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          value={field.value ?? ""}
                          onChange={(v) => {
                            field.onChange(v);
                            if (v) form.setValue("percentage", "");
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="percentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Percentage</FormLabel>
                      <FormControl>
                        <PercentageInput
                          value={field.value ?? ""}
                          onChange={(v) => {
                            field.onChange(v);
                            if (v) form.setValue("price", "");
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <p className="text-xs text-muted-foreground -mt-3">
                Price and percentage are mutually exclusive — setting one clears the other.
              </p>

              {/* Wood Species only: Finish Options */}
              {premiumType === "wood_species" && (
                <FormField
                  control={form.control}
                  name="finish_options"
                  render={() => (
                    <FormItem>
                      <FormLabel>Finish Options</FormLabel>
                      <FormControl>
                        <MultiSelectPopover
                          items={filteredFo}
                          selected={selectedFinishOptions}
                          onToggle={(id) => toggleInList("finish_options", id)}
                          search={foSearch}
                          onSearchChange={setFoSearch}
                          placeholder="Select finish options..."
                          searchPlaceholder="Search finish options..."
                          getLabel={(f) => f.code}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Door Style only: Wood Species */}
              {premiumType === "door_style" && (
                <FormField
                  control={form.control}
                  name="wood_species"
                  render={() => (
                    <FormItem>
                      <FormLabel>Wood Species</FormLabel>
                      <FormControl>
                        <MultiSelectPopover
                          items={filteredWs}
                          selected={selectedWoodSpecies}
                          onToggle={(id) => toggleInList("wood_species", id)}
                          search={wsSearch}
                          onSearchChange={setWsSearch}
                          placeholder="Select wood species..."
                          searchPlaceholder="Search wood species..."
                          getLabel={(w) => w.code}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Tags multi-select */}
              <FormField
                control={form.control}
                name="tags"
                render={() => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Popover>
                        <PopoverTrigger
                          className="flex min-h-7 w-full items-center gap-2 rounded-md border border-input bg-input/20 px-2 py-1 text-xs/relaxed cursor-pointer transition-colors hover:bg-input/30"
                        >
                          {selectedTags.length === 0 ? (
                            <span className="text-muted-foreground">Select tags...</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {selectedTags.map((t) => (
                                <Badge key={t.name} variant="secondary" className="text-xs">
                                  {t.value ? `${t.name}:${t.value}` : t.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-2" align="start">
                          <div className="relative mb-2">
                            <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              placeholder="Search tags..."
                              value={tagSearch}
                              onChange={(e) => setTagSearch(e.target.value)}
                              className="pl-8 h-8 text-xs"
                            />
                          </div>
                          <div className="max-h-[200px] overflow-y-auto space-y-0.5">
                            {filteredTags.map((tag) => (
                              <label key={tag.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50 cursor-pointer text-sm">
                                <Checkbox
                                  checked={selectedTags.some((t) => t.name === tag.name)}
                                  onCheckedChange={() => toggleTag(tag)}
                                />
                                {tag.display ?? tag.name}
                              </label>
                            ))}
                            {filteredTags.length === 0 && (
                              <p className="text-xs text-muted-foreground text-center py-2">No tags found</p>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <SheetFooter className="border-t pt-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" className="cursor-pointer" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="cursor-pointer" disabled={saving}>
                {saving ? "Saving..." : isEdit ? "Update" : "Create"}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}

// ─── Reusable multi-select popover ──────────────────────────

function MultiSelectPopover<T extends { id: number }>({
  items, selected, onToggle, search, onSearchChange, placeholder, searchPlaceholder, getLabel, selectedLabels,
}: {
  items: T[];
  selected: number[];
  onToggle: (id: number) => void;
  search: string;
  onSearchChange: (v: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  getLabel: (item: T) => string;
  /** Optional id→label map for selected items not in `items` (e.g. server-side search). */
  selectedLabels?: Map<number, string>;
}) {
  return (
    <Popover>
      <PopoverTrigger
        className="flex min-h-7 w-full items-center gap-2 rounded-md border border-input bg-input/20 px-2 py-1 text-xs/relaxed cursor-pointer transition-colors hover:bg-input/30"
      >
        {selected.length === 0 ? (
          <span className="text-muted-foreground">{placeholder}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {selected.map((id) => {
              const item = items.find((i) => i.id === id);
              const label = item ? getLabel(item) : selectedLabels?.get(id) ?? `#${id}`;
              return (
                <Badge key={id} variant="secondary" className="text-xs">{label}</Badge>
              );
            })}
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2" align="start">
        <div className="relative mb-2">
          <HugeiconsIcon icon={Search01Icon} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        <div className="max-h-[200px] overflow-y-auto space-y-0.5">
          {items.map((item) => (
            <label key={item.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50 cursor-pointer text-sm">
              <Checkbox
                checked={selected.includes(item.id)}
                onCheckedChange={() => onToggle(item.id)}
              />
              {getLabel(item)}
            </label>
          ))}
          {items.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No items found</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
